import { useEffect, useState } from 'react';
import { Button, Card, CardContent, InputAdornment, Stack, TextField, Typography } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { routesApi } from '@/api/subtours.api';
import DataState from '@/components/common/DataState';
import { useNotify } from '@/components/common/useNotify';
import { useAuth } from '@/auth/useAuth';

const BUILT_IN = 'The vehicle will go up to points where it is permitted. Remaining distance by walk or local transport.';

/**
 * Settings › Places & itinerary (Module 1, M1.4 / M1.5): the standard vehicle access note added to quotes and vouchers
 * for sub-tours, and the longest drive in one day before the itinerary builder suggests an overnight stop.
 */
export function PlacesDefaultsSection() {
  const qc = useQueryClient();
  const notify = useNotify();
  const { hasAnyPermission } = useAuth();
  const canEdit = hasAnyPermission(['Company.Edit']);
  const q = useQuery({ queryKey: ['travel-defaults'], queryFn: () => routesApi.defaults() });
  const [note, setNote] = useState('');
  const [hours, setHours] = useState(10);
  useEffect(() => { if (q.data) { setNote(q.data.vehicleAccessNote); setHours(q.data.maxDriveHoursPerDay); } }, [q.data]);

  const save = useMutation({ mutationFn: () => routesApi.saveDefaults({ vehicleAccessNote: note.trim() || BUILT_IN, maxDriveHoursPerDay: hours }) });
  const problem = hours < 4 || hours > 18 ? 'Day limit must be between 4 and 18 hours.' : note.length > 500 ? 'The note can have at most 500 characters.' : null;

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" fontWeight={700}>Places &amp; itinerary</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>Defaults used by sub-tours, travel times and the itinerary builder.</Typography>
        <DataState loading={q.isLoading} error={q.error} onRetry={q.refetch}>
          <Stack spacing={2.5} sx={{ maxWidth: 640 }}>
            <TextField label="Vehicle access note" multiline minRows={2} value={note} disabled={!canEdit} onChange={(e) => setNote(e.target.value)}
              helperText="Added to quotes and vouchers for sub-tours whose stops not every vehicle can reach." />
            <Stack direction="row" spacing={1}>
              <Button size="small" variant="text" disabled={!canEdit || note === BUILT_IN} onClick={() => setNote(BUILT_IN)}>Use the standard wording</Button>
            </Stack>
            <TextField label="Longest drive in one day" type="number" value={hours} disabled={!canEdit} onChange={(e) => setHours(Number(e.target.value))} sx={{ maxWidth: 260 }}
              slotProps={{ input: { endAdornment: <InputAdornment position="end">hours</InputAdornment> }, htmlInput: { min: 4, max: 18 } }}
              helperText="A longer drive suggests an overnight stop on the way." />
            {canEdit && (
              <Stack direction="row" spacing={1} alignItems="center">
                <Button variant="contained" disabled={!!problem || save.isPending} onClick={async () => {
                  try { await save.mutateAsync(); qc.invalidateQueries({ queryKey: ['travel-defaults'] }); notify.success('Places & itinerary settings saved.'); } catch (e) { notify.error(e); }
                }}>{save.isPending ? 'Saving…' : 'Save'}</Button>
                {problem && <Typography variant="body2" color="error">{problem}</Typography>}
              </Stack>
            )}
          </Stack>
        </DataState>
      </CardContent>
    </Card>
  );
}
